import React, { useRef, useEffect, useEffectEvent } from "react";
import clsx from "clsx";

export interface QualitySelectorProps {
  qualities: string[];
  currentQuality: string;
  showQualityMenu: boolean;
  onQualityChange: (quality: string) => void;
  onToggleMenu: () => void;
}

export const QualitySelector = React.memo(({
  qualities,
  currentQuality,
  showQualityMenu,
  onQualityChange,
  onToggleMenu,
}: QualitySelectorProps) => {
  const qualityRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useEffectEvent((event: MouseEvent) => {
    if (
      qualityRef.current &&
      !qualityRef.current.contains(event.target as Node) &&
      menuRef.current &&
      !menuRef.current.contains(event.target as Node)
    ) {
      if (showQualityMenu) {
        onToggleMenu();
      }
    }
  });

  useEffect(() => {
    if (showQualityMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showQualityMenu]);

  if (qualities.length <= 1) return null;

  const handleQualityChange = (quality: string) => {
    onQualityChange(quality);
    onToggleMenu();
  };

  return (
    <div className="relative">
      <button
        type="button"
        ref={qualityRef}
        onClick={onToggleMenu}
        className="text-white hover:text-gray-300 transition-colors text-xs px-2 py-1 border border-[#ffffff4d] rounded"
      >
        {currentQuality}
      </button>
      {showQualityMenu && (
        <div
          ref={menuRef}
          className="absolute bottom-full right-0 mb-2 bg-[#000000e6] border border-[#ffffff4d] rounded overflow-hidden"
        >
          {qualities.map((quality) => (
            <button
              type="button"
              key={quality}
              onClick={() => handleQualityChange(quality)}
              className={clsx(
                "block w-full text-left px-3 py-2 text-sm text-white hover:bg-[#ffffff33] transition-colors",
                quality === currentQuality && "bg-[#ffffff33]"
              )}
            >
              {quality}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

QualitySelector.displayName = "QualitySelector";
